import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideImagePlus, lucideTrash2, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { AuthenticatedImageComponent } from '../../../../components/authenticated-image/authenticated-image.component';
import {
  CompanyImageService,
  DesignImage,
  IMAGE_LIBRARY_MAX_BYTES,
} from '../../../../services/company-image.service';
import { validateImageFile } from '../../../../utils/image-file.utils';
import { LoggerService } from '../../../../services/logger.service';
import { designErrorMessage } from '../../../../services/voucher-design.service';
import { takeSelectedFile } from '../../../../utils/image-file.utils';

/**
 * The company's pictures: upload, pick one for the design, delete. A picture a
 * design still places cannot be deleted (the server refuses for saved layouts; the
 * editor's unsaved layout is passed in as `usedIds`).
 */
@Component({
  selector: 'app-image-library-dialog',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmDialogImports,
    HlmEmptyImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTooltipImports,
    HlmTypographyImports,
    AuthenticatedImageComponent,
  ],
  providers: [provideIcons({ lucideImagePlus, lucideTrash2, lucideTriangleAlert })],
  templateUrl: './image-library-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageLibraryDialogComponent {
  private readonly imageService = inject(CompanyImageService);
  private readonly logger = inject(LoggerService);

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  readonly open = input(false);
  /** Ids the current (also unsaved) layout places: those cannot be deleted here. */
  readonly usedIds = input<ReadonlySet<string>>(new Set());
  readonly maxImages = input(30);

  readonly pick = output<DesignImage>();
  readonly close = output<void>();

  readonly images = signal<DesignImage[] | null>(null);
  readonly error = signal<string | null>(null);
  readonly uploading = signal(false);
  readonly deletingId = signal<string | null>(null);
  readonly confirmDeleteId = signal<string | null>(null);

  readonly full = computed(() => (this.images()?.length ?? 0) >= this.maxImages());

  constructor() {
    effect(() => {
      if (!this.open()) return;
      untracked(() => {
        this.error.set(null);
        this.confirmDeleteId.set(null);
        this.load();
      });
    });
  }

  load() {
    this.imageService.list().subscribe({
      next: ({ images }) => this.images.set(images),
      error: (error) => {
        this.logger.error('Bilder konnten nicht geladen werden:', error);
        this.error.set('Die Bilder konnten nicht geladen werden.');
      },
    });
  }

  fileUrl(image: DesignImage): string {
    return this.imageService.fileUrl(image);
  }

  sizeLabel(image: DesignImage): string {
    return `${image.width_px} × ${image.height_px} px`;
  }

  openPicker() {
    this.fileInput()?.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const file = takeSelectedFile(event);
    if (!file) return;
    // type OR extension: iOS may report HEIC, some pickers no type at all; the
    // server decides by the bytes
    const problem = validateImageFile(file, IMAGE_LIBRARY_MAX_BYTES);
    if (problem) {
      this.error.set(problem);
      return;
    }
    this.error.set(null);
    this.uploading.set(true);
    this.imageService.upload(file).subscribe({
      next: (image) => {
        this.uploading.set(false);
        this.images.update((all) => [image, ...(all ?? [])]);
      },
      error: (error) => {
        this.logger.error('Bild-Upload fehlgeschlagen:', error);
        this.uploading.set(false);
        this.error.set(designErrorMessage(error, 'Das Bild konnte nicht hochgeladen werden.'));
      },
    });
  }

  askDelete(image: DesignImage) {
    this.confirmDeleteId.set(image.public_id);
  }

  confirmDelete(image: DesignImage) {
    this.confirmDeleteId.set(null);
    this.deletingId.set(image.public_id);
    this.error.set(null);
    this.imageService.delete(image.public_id).subscribe({
      next: () => {
        this.deletingId.set(null);
        this.images.update((all) => (all ?? []).filter((i) => i.public_id !== image.public_id));
      },
      error: (error) => {
        this.logger.error('Bild konnte nicht gelöscht werden:', error);
        this.deletingId.set(null);
        this.error.set(designErrorMessage(error, 'Das Bild konnte nicht gelöscht werden.'));
      },
    });
  }

  onDialogState(state: 'open' | 'closed') {
    if (state === 'closed' && this.open()) this.close.emit();
  }
}
